import {cn} from '../lib/utils'
import type {IconProps} from './Icon'
import {Loader} from './Loader'
import {cva, type VariantProps} from 'class-variance-authority'
import {cloneElement, forwardRef, isValidElement, type ReactNode} from 'react'

// `aria-disabled` mirrors every `disabled:` rule so links styled as buttons
// (which can't take the disabled attribute) still render the disabled state.
export const buttonVariants = cva(
  'ease-in-out relative flex items-center justify-center transition-[color,background-color,border-color] not-disabled:not-aria-disabled:cursor-pointer aria-disabled:cursor-not-allowed',
  {
    variants: {
      variant: {
        filled:
          'bg-button text-text-button hover:not-disabled:not-aria-disabled:bg-button-hover disabled:not-aria-busy:bg-button-disabled disabled:not-aria-busy:text-text-button-disabled aria-disabled:bg-button-disabled aria-disabled:text-text-button-disabled',
        outlined:
          'border border-border-button-secondary bg-transparent text-text-button-secondary hover:not-disabled:not-aria-disabled:border-border-button-secondary-hover hover:not-disabled:not-aria-disabled:bg-button-secondary-hover hover:not-disabled:not-aria-disabled:text-text-button-secondary-hover disabled:not-aria-busy:border-border-button-secondary-disabled disabled:not-aria-busy:bg-button-secondary-disabled disabled:not-aria-busy:text-text-button-secondary-disabled aria-disabled:border-border-button-secondary-disabled aria-disabled:bg-button-secondary-disabled aria-disabled:text-text-button-secondary-disabled',
        transparent:
          'border border-current bg-transparent text-current hover:not-disabled:not-aria-disabled:bg-current/10 disabled:opacity-50 aria-disabled:opacity-50',
        link: 'bg-transparent text-text hover:not-disabled:text-text-hover disabled:text-text-disabled',
        secondary:
          'border border-border-button-secondary bg-button-secondary text-text-button-secondary hover:not-disabled:not-aria-disabled:bg-background-gray-light disabled:not-aria-busy:border-transparent disabled:not-aria-busy:bg-button-secondary-disabled disabled:not-aria-busy:text-text-button-secondary-disabled aria-disabled:border-transparent aria-disabled:bg-button-secondary-disabled aria-disabled:text-text-button-secondary-disabled',
      },
      size: {
        s: 'h-36 text-xs',
        m: 'h-40 text-sm',
        l: 'h-44 text-sm',
      },
      shape: {
        default: 'rounded-md',
        pill: 'rounded-full',
      },
    },
    compoundVariants: [
      // Default padding for non-secondary variants
      {variant: 'filled', size: 's', className: 'px-16'},
      {variant: 'filled', size: 'm', className: 'px-20'},
      {variant: 'filled', size: 'l', className: 'px-20'},
      {variant: 'outlined', size: 's', className: 'px-16'},
      {variant: 'outlined', size: 'm', className: 'px-20'},
      {variant: 'outlined', size: 'l', className: 'px-20'},
      {variant: 'transparent', size: 's', className: 'px-16'},
      {variant: 'transparent', size: 'm', className: 'px-20'},
      {variant: 'transparent', size: 'l', className: 'px-20'},
      // Secondary variant with asymmetric padding for icons
      {variant: 'secondary', size: 's', className: 'pr-16 pl-14'},
      {variant: 'secondary', size: 'm', className: 'pr-20 pl-16'},
      {variant: 'secondary', size: 'l', className: 'pr-20 pl-16'},
    ],
    defaultVariants: {
      size: 'l',
      variant: 'filled',
      shape: 'default',
    },
  },
)

// Icon-only buttons are square (width equals height). The icon is 20px on
// the large and medium buttons and 16px on the small one.
// Figma: https://www.figma.com/design/qS60CfMVFGs07l2G3yfhsB/%F0%9F%92%A0-Components?node-id=5660-2288
const iconOnlyWidth = {s: 'w-36', m: 'w-40', l: 'w-44'} as const
const iconOnlyIconSize = {s: 'small', m: 'medium', l: 'medium'} as const

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  /** Icon element to display before the button text */
  leadingIcon?: ReactNode
  /** Icon element to display after the button text */
  trailingIcon?: ReactNode
  /** Square button with only an icon (pass it as `leadingIcon`). Always set an `aria-label`. */
  iconOnly?: boolean
  /** Show a loading indicator and hide the button content */
  loading?: boolean
}

/**
 * Figma: https://www.figma.com/design/qS60CfMVFGs07l2G3yfhsB/%F0%9F%92%A0-Components?node-id=7241-6099&m=dev
 */
export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant,
      size,
      shape,
      leadingIcon,
      trailingIcon,
      iconOnly,
      loading,
      disabled,
      children,
      ...props
    },
    ref,
  ) => {
    const hasIcon = leadingIcon || trailingIcon

    return (
      <button
        ref={ref}
        type="button"
        className={cn(
          buttonVariants({variant, size, shape}),
          iconOnly && ['px-0', iconOnlyWidth[size ?? 'l']],
          className,
        )}
        {...props}
        disabled={disabled || loading}
        aria-busy={loading || props['aria-busy'] || undefined}
      >
        <span
          className={cn(
            'flex items-center justify-center',
            // The bottom padding optically centers text; an icon alone needs none.
            !iconOnly && 'pb-1',
            hasIcon ? 'gap-6' : 'gap-8',
            loading && !iconOnly && 'invisible',
          )}
        >
          {iconOnly && isValidElement<IconProps>(leadingIcon)
            ? cloneElement(leadingIcon, {size: iconOnlyIconSize[size ?? 'l']})
            : leadingIcon}
          {children && (
            <span
              className={cn(
                'pb-2',
                (variant === 'secondary' || variant === 'link') && 'font-light',
              )}
            >
              {children}
            </span>
          )}
          {trailingIcon}
        </span>
        {loading && !iconOnly && <Loader className="absolute" />}
      </button>
    )
  },
)

Button.displayName = 'Button'
